export interface KitchenSetupInput {
  hasLocation: boolean
  hasPhoto: boolean
  /** Undefined while unknown: the step is left out rather than shown wrongly. */
  hasPhone?: boolean
  hasHours: boolean
  dishCount: number
  hasPayoutAccount: boolean
  /** Only counted while chef verification gates selling on this deployment. */
  verificationRequired: boolean
  verificationStatus: string
}

export interface KitchenSetupStep {
  key: string
  title: string
  hint: string
  action: string
  to: string
  done: boolean
}

/**
 * The steps between "account created" and "a customer can order", in the
 * order that unblocks the most: a location is needed before any dish can be
 * created, and a dish before there is anything to sell.
 */
export const kitchenSetupSteps = (input: KitchenSetupInput): KitchenSetupStep[] => {
  const steps: KitchenSetupStep[] = [
    { key: 'location', title: 'Add where you cook', hint: 'Customers collect their orders here.', action: 'Add', to: '/welcome', done: input.hasLocation },
    { key: 'dish', title: 'Add your first dish', hint: 'Start with the dish people already ask you for.', action: 'Add dish', to: '/menu?view=library', done: input.dishCount > 0 },
    { key: 'hours', title: 'Set your cooking hours', hint: 'Customers can order for now only while you’re open.', action: 'Set hours', to: '/schedule', done: input.hasHours },
    { key: 'photo', title: 'Add a photo', hint: 'People order more readily from a kitchen they can picture.', action: 'Add photo', to: '/welcome', done: input.hasPhoto },
    ...(input.hasPhone === undefined
      ? []
      : [{ key: 'phone', title: 'Add your phone number', hint: 'So we can reach you quickly about an order.', action: 'Add', to: '/welcome', done: input.hasPhone }]),
    { key: 'payout', title: 'Add your bank account', hint: 'This is where your earnings are paid.', action: 'Add account', to: '/wallet?tab=methods', done: input.hasPayoutAccount },
  ]
  if (input.verificationRequired) {
    steps.push({
      key: 'verification',
      title: 'Get verified',
      hint: input.verificationStatus === 'pending' ? 'Thanks. We’re reviewing your documents.' : 'Customers can find your kitchen once you’re approved.',
      action: input.verificationStatus === 'pending' ? 'View' : 'Start',
      to: '/verification',
      done: input.verificationStatus === 'verified',
    })
  }
  return steps
}
